import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Sparkles, Star } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';

/**
 * Componente de avaliação 1-5 estrelas + comentário opcional.
 * Usado na tela /lavagens/:id quando lavagem está CONCLUIDA. Momento emocional:
 * hero com Sparkles, estrelas grandes com hover scale, confirmação celebratória.
 */

interface Props {
  seqLavagem: number;
}

const FRASES_NOTA: Record<number, string> = {
  1: 'Sentimos muito, vamos investigar.',
  2: 'Pode melhorar, e a gente vai.',
  3: 'No esperado, mas dá pra surpreender.',
  4: 'Boa! Estamos quase no perfeito.',
  5: 'Brilhante! Você fez nosso dia.',
};

export function AvaliarLavagem({ seqLavagem }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [nota, setNota] = useState(0);
  const [hover, setHover] = useState(0);
  const [comentario, setComentario] = useState('');
  const [confirmado, setConfirmado] = useState(false);

  // Carrega avaliação existente (se já avaliou)
  const existente = useQuery({
    queryKey: ['avaliacao-lavagem', seqLavagem, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_avaliacoes_lavagem')
        .select('seq_avaliacao, val_nota, des_comentario, dta_avaliacao')
        .eq('seq_lavagem', seqLavagem)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setNota(data.val_nota);
        setComentario(data.des_comentario ?? '');
      }
      return data;
    },
    enabled: !!user,
  });

  const enviar = useMutation({
    mutationFn: async () => {
      if (nota < 1 || nota > 5) throw new Error('Selecione uma nota');
      const { data, error } = await supabase.rpc('fn_criar_avaliacao_lavagem', {
        p_seq_lavagem: seqLavagem,
        p_val_nota: nota,
        p_des_comentario: comentario.trim() || undefined,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      toast.success('Obrigado pela avaliação!');
      setConfirmado(true);
      setTimeout(() => setConfirmado(false), 2200);
      void qc.invalidateQueries({ queryKey: ['avaliacao-lavagem', seqLavagem] });
    },
    onError: (e) => toast.error('Falha ao avaliar: ' + (e as Error).message),
  });

  const exibida = hover || nota;
  const jaAvaliou = !!existente.data;

  return (
    <Card variant="default" className="relative overflow-hidden">
      {/* Mesh decorativo */}
      <div
        aria-hidden
        className="bg-octa-gradient pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-10 blur-3xl"
      />

      <CardContent className="relative space-y-5 p-6">
        {/* Hero */}
        <div className="flex flex-col items-center gap-2 text-center">
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
            className="bg-octa-gradient shadow-octa-glow flex h-12 w-12 items-center justify-center rounded-2xl text-white"
          >
            <Sparkles className="h-6 w-6" />
          </motion.div>
          <h3 className="text-display-sm font-extrabold tracking-tight">
            {jaAvaliou ? 'Sua avaliação' : 'Avalie está lavagem'}
          </h3>
          <p className="text-muted-foreground text-sm">
            {jaAvaliou
              ? 'Obrigado por ter compartilhado. Pode atualizar quando quiser.'
              : 'Como foi a experiência? Seu feedback ajuda muito.'}
          </p>
        </div>

        {/* Estrelas grandes */}
        <div
          className="flex justify-center gap-2"
          role="radiogroup"
          aria-label="Nota de 1 a 5 estrelas"
        >
          {[1, 2, 3, 4, 5].map((n) => {
            const ativa = n <= exibida;
            return (
              <motion.button
                key={n}
                type="button"
                role="radio"
                aria-checked={nota === n}
                aria-label={`${n} estrela${n > 1 ? 's' : ''}`}
                onClick={() => setNota(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                whileHover={{ scale: 1.15, y: -2 }}
                whileTap={{ scale: 0.9 }}
                animate={
                  nota === n
                    ? { scale: [1, 1.25, 1], rotate: [0, -8, 8, 0] }
                    : { scale: 1, rotate: 0 }
                }
                transition={{ duration: 0.35 }}
                className="focus-visible:ring-primary rounded-full p-1 focus-visible:outline-none focus-visible:ring-2"
              >
                <Star
                  className={`h-11 w-11 transition-colors ${
                    ativa
                      ? 'fill-amber-400 text-amber-400 drop-shadow-[0_4px_12px_rgba(251,191,36,0.45)]'
                      : 'text-muted-foreground/40'
                  }`}
                  strokeWidth={1.5}
                />
              </motion.button>
            );
          })}
        </div>

        {/* Frase contextual conforme nota */}
        <AnimatePresence mode="wait">
          {exibida > 0 && (
            <motion.p
              key={exibida}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="text-primary text-center text-sm font-semibold"
            >
              {FRASES_NOTA[exibida]}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Textarea ampla */}
        <div className="space-y-1.5">
          <label htmlFor="comentario" className="text-muted-foreground text-xs font-semibold">
            Quer contar mais? (opcional)
          </label>
          <textarea
            id="comentario"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="Comentario sobre a equipe, qualidade, atendimento..."
            rows={3}
            maxLength={500}
            aria-label="Comentario sobre a lavagem"
            className="border-input bg-background placeholder:text-muted-foreground/60 focus-visible:ring-primary shadow-octa-sm w-full resize-none rounded-xl border px-4 py-3 text-sm transition-all focus-visible:outline-none focus-visible:ring-2"
          />
          <p className="text-muted-foreground/70 text-right text-[10px]">{comentario.length}/500</p>
        </div>

        {/* CTA + confirmação */}
        <div className="relative">
          <motion.div whileTap={{ scale: 0.97 }}>
            <Button
              size="xl"
              onClick={() => enviar.mutate()}
              disabled={nota < 1 || enviar.isPending}
              className="bg-octa-gradient shadow-octa-glow w-full text-white"
            >
              {enviar.isPending
                ? 'Enviando...'
                : jaAvaliou
                  ? 'Atualizar avaliação'
                  : 'Enviar avaliação'}
            </Button>
          </motion.div>

          {/* Spring celebratório quando envia */}
          <AnimatePresence>
            {confirmado && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -8 }}
                transition={{ type: 'spring', stiffness: 380, damping: 18 }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="bg-octa-gradient shadow-octa-lg shadow-octa-glow flex items-center gap-2 rounded-2xl px-6 py-4 text-white">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  >
                    <CheckCircle2 className="h-6 w-6" />
                  </motion.div>
                  <span className="font-bold">Recebemos, obrigado!</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </CardContent>
    </Card>
  );
}
